import React, { useEffect, useState } from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import "./board-admin-page.scss";
import "./board-settings-page.scss";
import BoardColumnsEditor from "./board-columns-editor";
import { BoardService } from "./board-service";
import ConfirmDialog from "./confirm-dialog";
import { Board } from "./types";
import { useBoardConfig } from "./use-board";

interface BoardSettingsPageProps {
  board: Board;
  // Return to the board view.
  onExit: () => void;
  // Called after the board is deleted so the parent can navigate away.
  onDeleted: () => void;
}

// Board administration — owner-gated, board-specific configuration (reached via More → Board
// settings): the board itself (rename / delete) and its columns. Project-level concerns
// (statuses/workflow, releases, project rename/delete) and workspace-wide custom fields live in
// the project hub, not here.
const BoardSettingsPage: React.FC<BoardSettingsPageProps> = ({ board, onExit, onDeleted }) => {
  const { config } = useBoardConfig();
  const canManage: boolean = config.canManage;
  const [name, setName] = useState<string>(board.name);
  const [confirmDeleteBoard, setConfirmDeleteBoard] = useState<boolean>(false);

  useEffect((): void => {
    setName(board.name);
  }, [board.name]);

  const trimmedName: string = name.trim();
  const canRename: boolean = trimmedName.length > 0 && trimmedName !== board.name;

  return (
    <div className="kuika-board-admin-page">
      <div className="kuika-board-admin-page__head">
        <button type="button" className="kuika-board-admin-page__back" onClick={onExit}>
          <KuikaSvg name="arrow-left" type="arrows" width={15} height={15} />
          Back to board
        </button>
        <h2 className="kuika-board-admin-page__title">Board settings</h2>
      </div>

      <div className="kuika-board-admin-page__body kuika-board-settings">
        {!canManage && (
          <div className="kuika-board-settings__readonly-note">
            <KuikaSvg name="lock" width={13} height={13} />
            View only — workspace owners or supervisors can change board settings.
          </div>
        )}

        <section className="kuika-board-settings__section">
          <div className="kuika-board-settings__section-title">Board</div>
          {canManage ? (
            <>
              <div className="kuika-board-settings__section-sub">Rename this board, or remove it.</div>
              <div className="kuika-board-settings__rename">
                <KuikaInput value={name} onChange={(_event, value: string | number) => setName(String(value))} />
                <KuikaButton
                  type="primary"
                  onClick={() => {
                    if (canRename) BoardService.renameBoard(board.id, trimmedName);
                  }}
                  disabled={!canRename}
                >
                  Rename
                </KuikaButton>
              </div>
              <button
                type="button"
                className="kuika-board-settings__danger-link"
                onClick={() => setConfirmDeleteBoard(true)}
              >
                Delete board
              </button>
            </>
          ) : (
            <div className="kuika-board-settings__section-sub">{board.name}</div>
          )}
        </section>

        <section className="kuika-board-settings__section">
          <BoardColumnsEditor board={board} canManage={canManage} />
        </section>
      </div>

      <ConfirmDialog
        open={confirmDeleteBoard}
        title="Delete board?"
        message={`"${board.name}" will be removed. Its issues stay — only this board view is deleted.`}
        confirmLabel="Delete board"
        cancelLabel="Keep"
        danger
        onConfirm={() => {
          BoardService.deleteBoard(board.id);
          setConfirmDeleteBoard(false);
          onDeleted();
        }}
        onCancel={() => setConfirmDeleteBoard(false)}
      />
    </div>
  );
};

export default BoardSettingsPage;
